import { Box, Skeleton, Stack, Typography } from '@mui/material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { alpha } from '@mui/material/styles';
import { TrackerDataType } from '../../shared/constants/constants';
import {
  getQueryAdderUiPalette,
  queryAdderPalette,
  queryAdderTypeScale,
} from '../../shared/constants/queryAdderPalette';
import {
  QueryAdderModalShell,
  SectionCard,
  queryAdderFooterActionSx,
  queryAdderTwoColumnGridSx,
} from './QueryAdderLayoutShared';
import { adderHeaderTitleSx, queryAdderPopupEyebrowSx, queryAdderPopupIconSx } from './queryAdderPopupStyles';

interface QueryAdderDrawerSkeletonProps {
  title: string;
  eventHandlerType?: TrackerDataType;
  isLinkedToService?: boolean;
}

const topicLinkingInheritedSkeletonKeys = [
  'topic-linking-inherited-topic',
  'topic-linking-inherited-service',
  'topic-linking-inherited-status',
  'topic-linking-inherited-owner',
];
const serviceDetailSkeletonKeys = [
  'service-detail-topic',
  'service-detail-status',
  'service-detail-type',
  'service-detail-lead',
  'service-detail-team',
  'service-detail-unit',
  'service-detail-campaign',
  'service-detail-date',
  'service-detail-due-date',
  'service-detail-notes',
];
const trackerDetailSkeletonKeys = [
  'tracker-detail-topic',
  'tracker-detail-status',
  'tracker-detail-type',
  'tracker-detail-owner',
  'tracker-detail-contact',
  'tracker-detail-date',
  'tracker-detail-due-date',
  'tracker-detail-notes',
];
const serviceMaterialSkeletonKeys = ['service-material-primary', 'service-material-secondary'];
const trackerMaterialSkeletonKeys = [
  'tracker-material-primary',
  'tracker-material-secondary',
  'tracker-material-expanded',
];

const queryAdderSkeletonSectionTitleSx = {
  ...queryAdderPopupEyebrowSx,
  mt: 0,
  fontSize: queryAdderTypeScale.helperText,
  fontWeight: 400,
  letterSpacing: '0.12em',
  lineHeight: 1.25,
};

function QueryAdderSkeletonField({ ui }: Readonly<{ ui: Record<string, any> }>) {
  return (
    <Box>
      <Skeleton variant="text" width="34%" height={18} sx={{ mb: 0.75, bgcolor: alpha(ui.main, 0.08) }} />
      <Skeleton
        variant="rounded"
        width="100%"
        height={44}
        sx={{ borderRadius: '10px', bgcolor: alpha(ui.main, 0.07) }}
      />
    </Box>
  );
}

function QueryAdderDrawerHeaderSkeleton({
  title,
  ui,
}: Readonly<{
  title: string;
  ui: Record<string, any>;
}>) {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: ui.white,
        minHeight: 96,
        borderTop: `6px solid ${ui.accentDark || ui.main}`,
        borderBottom: `1px solid ${ui.headerBorder}`,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{ width: '100%', px: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              ...queryAdderPopupIconSx,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(ui.main, 0.14),
            }}
          >
            <Skeleton variant="circular" width={22} height={22} sx={{ bgcolor: alpha(ui.main, 0.16) }} />
          </Box>
          <Typography
            data-cy="queryAdder-title"
            sx={{
              ...adderHeaderTitleSx,
              mt: 0,
              color: ui.title,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Editing {title}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function QueryAdderDrawerFooterSkeleton({ ui }: Readonly<{ ui: Record<string, any> }>) {
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        borderTop: `1px solid ${ui.headerBorder}`,
        backgroundColor: ui.white,
        boxShadow: '0 -6px 18px rgba(15, 23, 42, 0.05)',
        px: 3,
        py: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} justifyContent="flex-end" flexWrap="wrap">
        <Skeleton variant="rounded" sx={{ ...queryAdderFooterActionSx, bgcolor: alpha(ui.main, 0.07) }} />
        <Skeleton variant="rounded" sx={{ ...queryAdderFooterActionSx, bgcolor: alpha(ui.main, 0.14) }} />
      </Stack>
    </Box>
  );
}

function QueryAdderTopicLinkingDrawerSkeleton({
  ui,
  isLinkedToService,
}: Readonly<{
  ui: Record<string, any>;
  isLinkedToService: boolean;
}>) {
  const serviceColor = ui.service || queryAdderPalette.service.main;
  const serviceTile = ui.serviceTile || queryAdderPalette.service.tile;
  const settingRowBorderColor = alpha(serviceColor, 0.26);
  const settingRowSurface = alpha(serviceColor, 0.08);

  return (
    <Box
      sx={{
        border: `1px solid ${ui.cardBorder}`,
        borderRadius: '18px',
        backgroundColor: ui.white,
        boxShadow: ui.sectionShadow || '0 1px 2px rgba(16,62,122,0.04)',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${ui.divider}` }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: serviceTile,
            color: queryAdderPalette.service.main,
            flexShrink: 0,
          }}
        >
          <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
        </Box>
        <Typography sx={{ ...queryAdderSkeletonSectionTitleSx, color: ui.title }}>Topic Linking</Typography>
      </Stack>

      <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            border: `1px solid ${settingRowBorderColor}`,
            backgroundColor: settingRowSurface,
            borderRadius: '12px',
            px: 2.25,
            py: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="text" width={210} height={22} sx={{ bgcolor: alpha(serviceColor, 0.1) }} />
            <Skeleton variant="text" width="58%" height={18} sx={{ mt: 0.5, bgcolor: alpha(serviceColor, 0.08) }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={58}
            height={34}
            sx={{ borderRadius: 999, bgcolor: alpha(serviceColor, 0.12) }}
          />
        </Box>

        {isLinkedToService ? (
          <Box
            sx={{ mt: 2, border: `1px solid ${ui.divider}`, borderRadius: '16px', backgroundColor: ui.softGray, p: 2 }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center" flexWrap="wrap">
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Skeleton variant="text" width="42%" height={24} sx={{ bgcolor: alpha(serviceColor, 0.1) }} />
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Skeleton
                    variant="rounded"
                    width={86}
                    height={24}
                    sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
                  />
                  <Skeleton
                    variant="rounded"
                    width={112}
                    height={24}
                    sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
                  />
                </Stack>
              </Box>
              <Skeleton
                variant="rounded"
                width={112}
                height={30}
                sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
              />
              <Skeleton
                variant="rounded"
                width={34}
                height={30}
                sx={{ borderRadius: '8px', bgcolor: alpha(serviceColor, 0.08) }}
              />
            </Stack>

            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${ui.divider}` }}>
              <Box sx={queryAdderTwoColumnGridSx}>
                {topicLinkingInheritedSkeletonKeys.map((skeletonKey) => (
                  <QueryAdderSkeletonField key={skeletonKey} ui={ui} />
                ))}
              </Box>
            </Box>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default function QueryAdderDrawerSkeleton({
  title,
  eventHandlerType = TrackerDataType.SERVICE,
  isLinkedToService = false,
}: Readonly<QueryAdderDrawerSkeletonProps>) {
  const ui = getQueryAdderUiPalette(eventHandlerType);
  const isService = eventHandlerType === TrackerDataType.SERVICE;
  const shouldShowTopicLinking = isService === false;
  const detailSkeletonKeys = isService ? serviceDetailSkeletonKeys : trackerDetailSkeletonKeys;
  const materialSkeletonKeys = isService ? serviceMaterialSkeletonKeys : trackerMaterialSkeletonKeys;

  return (
    <Box data-cy="query-adder-drawer-skeleton" sx={{ height: '100%', minHeight: 0, backgroundColor: ui.white }}>
      <QueryAdderModalShell
        header={<QueryAdderDrawerHeaderSkeleton title={title} ui={ui} />}
        footer={<QueryAdderDrawerFooterSkeleton ui={ui} />}
        bodySx={{ backgroundColor: ui.white }}
        contentSx={{ pb: 4, backgroundColor: ui.white }}
      >
        <Stack spacing={3} aria-busy="true" aria-label={`Loading ${title} details`}>
          {shouldShowTopicLinking ? (
            <QueryAdderTopicLinkingDrawerSkeleton ui={ui} isLinkedToService={isLinkedToService} />
          ) : null}

          <SectionCard
            title={isService ? 'Service Details' : 'Details'}
            icon={<AssignmentOutlinedIcon sx={{ fontSize: 20 }} />}
            ui={ui}
          >
            <Box sx={queryAdderTwoColumnGridSx}>
              {detailSkeletonKeys.map((skeletonKey) => (
                <QueryAdderSkeletonField key={skeletonKey} ui={ui} />
              ))}
            </Box>
          </SectionCard>

          <SectionCard title="Working Materials" icon={<FolderOutlinedIcon sx={{ fontSize: 20 }} />} ui={ui}>
            <Stack spacing={2}>
              {materialSkeletonKeys.map((skeletonKey, index) => (
                <Skeleton
                  key={skeletonKey}
                  variant="rounded"
                  width="100%"
                  height={index === materialSkeletonKeys.length - 1 ? 88 : 48}
                  sx={{ borderRadius: '10px', bgcolor: alpha(ui.main, 0.06) }}
                />
              ))}
            </Stack>
          </SectionCard>
        </Stack>
      </QueryAdderModalShell>
    </Box>
  );
}
